import {
  action,
  computed,
  makeObservable,
  observable,
  onBecomeObserved,
} from "mobx"

import { notify } from "@src/app/components/notify"
import { catchErrors, subMerchantApi } from "@src/shared/api"
import {
  SubmerchantRegisterRequest,
  SubmerchantResponse,
  SubmerchantResponseStatusEnum,
} from "@src/shared/api/generated"
import { loading } from "@src/shared/classes/utils/loading"

export type SubMerchUpdateRequest = {
  access_control_groups: SubmerchantRegisterRequest["access_control_groups"]
  status: SubmerchantResponse["status"]
}

export type SubMerchChangePasswordRequest = {
  /**
   *
   * @type {string}
   * @memberof InlineObject14
   */
  confirm_submerchant_password: string
  /**
   *
   * @type {string}
   * @memberof InlineObject14
   */
  current_merchant_password: string
  /**
   *
   * @type {string}
   * @memberof InlineObject14
   */
  new_submerchant_password: string
}

export class SubMerchant extends loading(["load", "create", "update"]) {
  data: SubmerchantResponse | null = null

  constructor(id: string = "") {
    super()

    makeObservable(this, {
      availableUpdateStatusOptions: computed,
      create: action.bound,
      data: observable,
      id: computed,
      load: action.bound,
      update: action.bound,
    })

    onBecomeObserved(this, "isLoading", () => {
      if (id) {
        this.load(id).then(() => this.setIsReadonly(true))
      }
    })
  }

  async changePassword(request: SubMerchChangePasswordRequest) {
    if (this.id) {
      return subMerchantApi
        .changeSubmerchantPassword(this.id, request)
        .then((response) => response)
        .catch((e) => catchErrors(e))
    }

    return undefined
  }

  async create(request: SubmerchantRegisterRequest) {
    return subMerchantApi
      .submerchantRegister({
        ...request,
      })
      .then(({ data: { data: res } }) => (this.data = res))
      .catch((e) => catchErrors(e))
  }

  async load(id: string) {
    try {
      const {
        data: { data: response },
      } = await subMerchantApi.getSubmerchant(id)

      if (response) {
        this.data = {
          ...response,
        }
      } else {
        notify.error()
      }
    } catch (e: any) {
      catchErrors(e)
    }
  }

  async update(id: string, request: SubMerchUpdateRequest) {
    return subMerchantApi
      .updateSubmerchant(id, request as any)
      .then(({ data: { data: res } }) => (this.data = res))
      .catch((e) => catchErrors(e))
  }

  get availableUpdateStatusOptions() {
    return [
      {
        disabled: true,
        label: "Pending",
        value: SubmerchantResponseStatusEnum.Pending,
      },
      {
        disabled: true,
        label: "Unconfirmed",
        value: SubmerchantResponseStatusEnum.Unconfirmed,
      },
      {
        label: "Blocked",
        value: SubmerchantResponseStatusEnum.Blocked,
      },
      {
        label: "Confirmed",
        value: SubmerchantResponseStatusEnum.Confirmed,
      },
    ]
  }

  get id() {
    return this.data?.id ?? null
  }

  get isNew() {
    return !this.data
  }
}
